import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/* ----------------------------------------------------------
   Dedicated landing page for Maschinenbau & technischer
   Mittelstand. Uses inline DE/EN copy (not global i18n JSON)
   because the story here is vertical-specific.
----------------------------------------------------------- */

type Lang = "de" | "en";

const useLang = (): Lang => {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith("de") ? "de" : "en";
};

type Copy = {
  eyebrow: string;
  h1a: string; h1b: string; h1hl: string; h1c: string;
  lead: string; cta: string; ctaNote: string; erpLine: string;
  stats: { num: string; label: string; src: string }[];
  loop: { title: string; running: string };
  stages: { icon: string; name: string; sub: string; gate?: boolean }[];
  gateTag: string;
  log: Record<"detect" | "trigger" | "draft" | "approve" | "execute" | "measure", string>;
  problem: {
    eyebrow: string; h2a: string; h2b: string; lead: string;
    cards: { tag: string; title: string; body: string }[];
    note1: string; note2: string;
  };
  mechanism: {
    eyebrow: string; h2: string; lead: string;
    rows: { name: string; desc: string; gate?: boolean }[];
  };
  outcome: {
    eyebrow: string; h2: string;
    cards: { ic: string; title: string; body: string }[];
  };
  proof: {
    eyebrow: string; h2: string; lead: string;
    systems: { name: string; body: string; specs: string[]; hidden?: boolean }[];
    claim: string;
  };
  mid: { eyebrow: string; h3: string; lead: string; cta: string };
  phases: {
    eyebrow: string; h2: string; lead: string;
    p: { days: string; title: string; bullets: string[] }[];
  };
  buying: { eyebrow: string; h2: string; p1: string; p2: string; pills: string[] };
  security: {
    eyebrow: string; h2: string; lead: string;
    cards: { ic: string; title: string; body: string }[];
  };
  guarantee: { eyebrow: string; h2: string; body: string };
  operator: {
    eyebrow: string; h2a: string; h2b: string; lead: string;
    facts: { k: string; v: string }[];
  };
  final: {
    brand: string; h2a: string; h2b: string; h2hl: string; h2c: string;
    lead: string; cta: string; ctaNote: string; foerder: string; sticky: string;
  };
};

const copy: Record<Lang, Copy> = {
  de: {
    eyebrow: "FÜR GESCHÄFTSFÜHRER IM MASCHINENBAU & TECHNISCHEN MITTELSTAND",
    h1a: "Ihr Angebotsprozess —",
    h1b: "läuft als autonomes ",
    h1hl: "System.",
    h1c: "In 90 Tagen live.",
    lead:
      "Ein kompletter Job in Ihrem Unternehmen — von Anfrage bis versandreifem Angebot — übernommen durch ein autonomes Agenten-System. In Ihrer Cloud. Mit Ihrem Prozesswissen. Zum Festpreis.",
    cta: "Erstgespräch anfragen",
    ctaNote: "// 30 Min. — kein Verkaufsgespräch",
    erpLine:
      "// Anbindung: proALPHA · abas · SAP Business One · Dynamics 365 · Ihr Postfach",
    stats: [
      {
        num: "24–72 h",
        label: "braucht ein technisches Angebot heute im Schnitt.",
        src: "Branchenbenchmark technischer B2B-Vertrieb",
      },
      {
        num: "82 %",
        label: "der B2B-Kunden erwarten eine Reaktion innerhalb von Minuten.",
        src: "Salesforce State of the Connected Customer",
      },
      {
        num: "178.000",
        label: "Fachkräfte verliert der Maschinenbau netto bis 2034.",
        src: "IW Köln, 2024",
      },
    ],
    loop: {
      title: "system/loop — angebots_runner",
      running: "RUNNING",
    },
    stages: [
      { icon: "◉", name: "detect", sub: "Anfrage erkannt" },
      { icon: "⚡", name: "trigger", sub: "Job gestartet" },
      { icon: "✎", name: "draft", sub: "Angebot entworfen" },
      { icon: "✓", name: "approve", sub: "Freigabe Vertrieb", gate: true },
      { icon: "▶", name: "execute", sub: "Versand + ERP" },
      { icon: "◫", name: "measure", sub: "Reaktion tracken" },
    ],
    gateTag: "HITL",
    log: {
      detect: "E-Mail von kunde@maschinenbau-x.de — Anfrage ‚Sonderfertigung Flansch DN80'",
      trigger: "Job #A-4821 in Warteschlange — Priorität hoch",
      draft: "Stückliste, Kalkulation, Angebotstext aus ERP-Historie generiert",
      approve: "Freigabe an vertrieb@ihre-firma.de gesendet — wartet auf OK",
      execute: "Angebot als PDF versandt · ERP-Vorgang #V-2039 angelegt",
      measure: "Reaktionszeit 4 Min. 12 Sek. · unter Zielwert",
    },

    problem: {
      eyebrow: "// DIAGNOSE",
      h2a: "Das Problem ist nicht KI.",
      h2b: "Das Problem ist Pilot-Purgatorium.",
      lead:
        "Sie haben ChatGPT-Lizenzen. Vielleicht sogar einen PoC. Nichts davon läuft im Kerngeschäft — weil ein Chat kein Prozess ist und ein Pilot kein System.",
      cards: [
        {
          tag: "// PoC-Falle",
          title: "Mindestens die Hälfte wird nie produktiv.",
          body:
            "Gartner: Bis Ende 2025 werden mindestens 50 % aller GenAI-Projekte nach dem Pilot abgebrochen. Grund: schlechter Daten-/Prozess-Fit, unklarer ROI, keine Integration.",
        },
        {
          tag: "// Chat-Falle",
          title: "80,3 % nutzen KI. Aber nicht im Prozess.",
          body:
            "RAND/Stanford: 80,3 % der Mitarbeiter nutzen generative KI — als Assistent im Browser. Der Angebotsprozess selbst läuft weiter per Hand, Excel und Bauchgefühl.",
        },
        {
          tag: "// Fachkräfte-Falle",
          title: "Wissen geht in Rente, bevor es dokumentiert ist.",
          body:
            "Der Kollege, der ‚alles im Kopf hat', kalkuliert Sonderteile aus dem Bauch. Wenn er geht, geht die Marge mit. Ein System hält das Wissen fest — und wendet es weiter an.",
        },
      ],
      note1:
        "Wir bauen keinen weiteren Piloten. Wir übernehmen einen Job — nicht als Assistent, sondern als System. Es läuft, es hat Freigabestellen, es integriert sich in Ihr ERP.",
      note2:
        "Am Ende der 90 Tage läuft es. Oder Sie zahlen nicht.",
    },

    mechanism: {
      eyebrow: "// SO FUNKTIONIERT ES",
      h2: "Sechs Stufen. Eine Schleife.",
      lead:
        "Der Angebotsprozess wird zerlegt, jede Stufe autonom ausgeführt, eine Stufe bleibt bei Ihnen: die Freigabe.",
      rows: [
        { name: "detect", desc: "System hört Ihr Postfach / Formular / EDI ab und erkennt Anfragen automatisch." },
        { name: "trigger", desc: "Für jede Anfrage wird ein Job angelegt, priorisiert und in die Warteschlange gestellt." },
        { name: "draft", desc: "Aus Stammdaten, ERP-Historie und Ihren Kalkulations-Regeln entsteht ein versandreifes Angebot." },
        { name: "approve", desc: "Vertrieb bekommt das Angebot mit einem Klick zur Freigabe. Diese Stufe bleibt beim Menschen.", gate: true },
        { name: "execute", desc: "Nach OK wird versendet, im ERP als Vorgang angelegt, im CRM verknüpft — ohne manuelles Copy-Paste." },
        { name: "measure", desc: "Reaktionszeit, Trefferquote, Marge werden pro Job gemessen. Sie sehen es im Dashboard." },
      ],
    },

    outcome: {
      eyebrow: "// ERGEBNIS",
      h2: "Was Sie am Tag 91 haben.",
      cards: [
        {
          ic: "4 MIN",
          title: "Reaktionszeit statt 24–72 Stunden.",
          body:
            "Anfragen werden autonom bearbeitet und liegen zur Freigabe vor, während der Wettbewerb noch die Excel-Datei sucht.",
        },
        {
          ic: "24/7",
          title: "Ein Vertriebs-Innendienst, der nicht schläft.",
          body:
            "Nacht, Wochenende, Brückentag — der Job läuft weiter. Ihre Mitarbeiter arbeiten an Kunde und Marge, nicht am Formular.",
        },
        {
          ic: "178k",
          title: "Wissen bleibt im Haus.",
          body:
            "Der Maschinenbau verliert bis 2034 netto 178.000 Fachkräfte. Der Kollege, der ‚alles im Kopf hat', ist einer davon — sein Prozesswissen läuft dann als System weiter.",
        },
      ],
    },

    proof: {
      eyebrow: "// EIGENE SYSTEME LIVE",
      h2: "Wir schreiben nicht darüber. Wir betreiben es.",
      lead:
        "Zwei autonome Systeme unter der Marke Raw Response Labs — im Dauerbetrieb, nicht im Slide.",
      systems: [
        {
          name: "webblitz.ai",
          body:
            "Autonomes AI-Visibility-System. Kontinuierliches Monitoring, Analyse und Optimierung über fünf AI-Plattformen — mit Approve-Gate vor jeder Auslieferung.",
          specs: ["pg-boss", "5 AI-Plattformen", "Approve-Gate"],
        },
        {
          name: "Project L.",
          hidden: true,
          body:
            "Agentic Newsletter-Betrieb. MCP-nativ. Ein Sponsor-Agent verhandelt und disponiert Platzierungen autonom.",
          specs: ["Agentic", "MCP-native", "Sponsor-Agent"],
        },
      ],
      claim: "Wir kennen die Schleife, weil wir sie täglich fahren.",
    },

    mid: {
      eyebrow: "// NÄCHSTER SCHRITT",
      h3: "Passt Ihr Angebotsprozess in die 90 Tage?",
      lead:
        "30 Min. Erstgespräch. Wir schauen uns Ihren aktuellen Prozess an und sagen ehrlich, ob und wo es sich lohnt.",
      cta: "Erstgespräch anfragen",
    },

    phases: {
      eyebrow: "// PROGRAMM",
      h2: "90 Tage. Drei Phasen. Ein Festpreis. Am Ende läuft es.",
      lead:
        "Keine Roadmap, kein PoC, kein ‚wir sehen dann weiter'. Ein klarer Bauplan mit definiertem Ende.",
      p: [
        {
          days: "TAG 1–30",
          title: "Prozess-Cut.",
          bullets: [
            "Einen Job auswählen und schneiden (Angebot, Reklamation, Auftrag).",
            "Datenpfade & ERP-Anbindung klären.",
            "Approve-Gate mit Ihrem Vertrieb definieren.",
          ],
        },
        {
          days: "TAG 31–60",
          title: "Build.",
          bullets: [
            "Loop implementieren, in Ihrer Cloud, mit Ihren Daten.",
            "Regeln & Kalkulation aus Ihrer ERP-Historie ableiten.",
            "Wöchentliche Demos — kein Blackbox-Build.",
          ],
        },
        {
          days: "TAG 61–90",
          title: "Live.",
          bullets: [
            "Schrittweise Übergabe: Schatten-Betrieb, dann Voll-Betrieb.",
            "Betriebshandbuch, Monitoring, Alerting.",
            "Ihr Team betreibt es. Wir bleiben optional als Service.",
          ],
        },
      ],
    },

    buying: {
      eyebrow: "// SO KAUFEN SIE DAS",
      h2: "Kein Abo. Kein Preis pro Dokument. Keine Mindestlaufzeit.",
      p1:
        "Ein Festpreis für den 90-Tage-Build. Das System läuft in Ihrer Cloud und gehört Ihnen — Betrieb und Weiterentwicklung optional als monatlicher Service.",
      p2:
        "Vergleichbare Angebots-Automatisierungen amortisieren sich in Monaten, nicht Jahren.",
      pills: ["Festpreis", "In Ihrer Cloud", "Ihr Eigentum", "Betrieb optional"],
    },

    security: {
      eyebrow: "// SICHERHEIT & KONTROLLE",
      h2: "Ihre Cloud. Ihre Daten. Ihre Freigabe.",
      lead:
        "Das System läuft dort, wo Ihre Daten ohnehin liegen. Keine Datenabflüsse in fremde SaaS-Silos.",
      cards: [
        {
          ic: "CLOUD",
          title: "In Ihrer Cloud (AWS / Azure / GCP / on-prem).",
          body: "Deployment in Ihrer Umgebung. Sie behalten Root, Netzwerk, Keys. DSGVO-konform.",
        },
        {
          ic: "HITL",
          title: "Human-in-the-Loop-Gate an kritischen Stellen.",
          body: "Freigabe bleibt beim Menschen. Kein Angebot, keine Bestellung ohne Ihr OK.",
        },
        {
          ic: "AUDIT",
          title: "Volle Nachvollziehbarkeit pro Job.",
          body: "Jeder Schritt geloggt, jede Entscheidung begründet, revisionssicher archiviert.",
        },
      ],
    },

    guarantee: {
      eyebrow: "// GARANTIE",
      h2: "Am Tag 91 läuft es. Oder Sie zahlen nicht.",
      body:
        "Wenn das System nach 90 Tagen nicht produktiv im Betrieb ist, bekommen Sie den Festpreis zurück. Ohne Diskussion.",
    },

    operator: {
      eyebrow: "// OPERATOR",
      h2a: "Wer das baut — und",
      h2b: "wer dafür haftet.",
      lead:
        "Kein Team, kein White-Label, keine Agentur-Kette. Ein Operator, der die Systeme selbst betreibt und für den Ausgang haftet.",
      facts: [
        { k: "OPERATOR", v: "Levent Valente — Founder, Raw Response Labs UG (haftungsbeschränkt)" },
        { k: "BACKGROUND", v: "10+ Jahre Software & Automatisierung. Systeme, die produktiv Umsatz produzieren." },
        { k: "STACK", v: "TypeScript, Python, pg-boss, MCP, LLM-Orchestrierung — in Ihrer Cloud." },
        { k: "PRINZIP", v: "Ein Job, ein System, ein Verantwortlicher. Keine Slides, kein PoC-Karussell." },
      ],
    },

    final: {
      brand: "// RAW RESPONSE LABS × IHR MASCHINENBAU",
      h2a: "Ein Job. Ein System.",
      h2b: "Ein ",
      h2hl: "Festpreis.",
      h2c: " 90 Tage.",
      lead:
        "30 Min. Erstgespräch. Wir schauen uns einen konkreten Prozess an. Am Ende wissen Sie, ob und wie sich das für Sie rechnet.",
      cta: "Erstgespräch anfragen",
      ctaNote: "// Kein Verkaufsgespräch. Kein Pitch. Ein Blick auf Ihren Prozess.",
      foerder:
        "// Im Erstgespräch prüfen wir mit, welche Landesförderung für Ihr Projekt greift.",
      sticky: "Erstgespräch",
    },
  },

  en: {
    eyebrow: "FOR MANAGING DIRECTORS IN MECHANICAL ENGINEERING & TECHNICAL MITTELSTAND",
    h1a: "Your quoting process —",
    h1b: "run by an autonomous ",
    h1hl: "system.",
    h1c: "Live in 90 days.",
    lead:
      "One complete job in your company — from RFQ to send-ready quote — taken over by an autonomous agent system. In your cloud. With your process knowledge. At a fixed price.",
    cta: "Book intro call",
    ctaNote: "// 30 min — no sales pitch",
    erpLine:
      "// Integrations: proALPHA · abas · SAP Business One · Dynamics 365 · your inbox",
    stats: [
      { num: "24–72 h", label: "average turnaround for a technical quote today.", src: "Technical B2B sales benchmark" },
      { num: "82 %", label: "of B2B customers expect a response within minutes.", src: "Salesforce State of the Connected Customer" },
      { num: "178,000", label: "net skilled workers German mechanical engineering will lose by 2034.", src: "IW Köln, 2024" },
    ],
    loop: { title: "system/loop — quote_runner", running: "RUNNING" },
    stages: [
      { icon: "◉", name: "detect", sub: "RFQ detected" },
      { icon: "⚡", name: "trigger", sub: "Job queued" },
      { icon: "✎", name: "draft", sub: "Quote drafted" },
      { icon: "✓", name: "approve", sub: "Sales approve", gate: true },
      { icon: "▶", name: "execute", sub: "Send + ERP" },
      { icon: "◫", name: "measure", sub: "Track response" },
    ],
    gateTag: "HITL",
    log: {
      detect: "Email from customer@machinery-x.com — RFQ ‘Custom flange DN80'",
      trigger: "Job #A-4821 queued — high priority",
      draft: "BOM, calc, quote text generated from ERP history",
      approve: "Sent to sales@yourcompany.com — awaiting OK",
      execute: "PDF sent · ERP record #V-2039 created",
      measure: "Response time 4 min 12 sec · under target",
    },
    problem: {
      eyebrow: "// DIAGNOSIS",
      h2a: "The problem isn't AI.",
      h2b: "The problem is pilot purgatory.",
      lead: "You have ChatGPT licenses. Maybe a PoC. None of it runs in the core business — because a chat isn't a process and a pilot isn't a system.",
      cards: [
        { tag: "// PoC trap", title: "At least half never make it to production.", body: "Gartner: by end of 2025, at least 50% of GenAI projects will be abandoned after the pilot. Poor data/process fit, unclear ROI, no integration." },
        { tag: "// Chat trap", title: "80.3% use AI. But not in the process.", body: "RAND/Stanford: 80.3% of employees use generative AI — as a browser assistant. The quoting process itself still runs on hand, Excel and gut feel." },
        { tag: "// Talent trap", title: "Knowledge retires before it's documented.", body: "The colleague who ‘has it all in his head' quotes custom parts from experience. When he leaves, the margin leaves with him. A system holds and applies that knowledge." },
      ],
      note1: "We don't build another pilot. We take over a job — not as an assistant, but as a system. It runs, it has approval gates, it integrates with your ERP.",
      note2: "At the end of 90 days it runs. Or you don't pay.",
    },
    mechanism: {
      eyebrow: "// HOW IT WORKS",
      h2: "Six stages. One loop.",
      lead: "The quoting process is decomposed, each stage runs autonomously, one stage stays with you: the approval.",
      rows: [
        { name: "detect", desc: "System listens to inbox / form / EDI and detects RFQs automatically." },
        { name: "trigger", desc: "For each RFQ a job is created, prioritised and queued." },
        { name: "draft", desc: "From master data, ERP history and your calculation rules a send-ready quote is drafted." },
        { name: "approve", desc: "Sales approves in one click. This stage stays with the human.", gate: true },
        { name: "execute", desc: "After OK the quote goes out, ERP record is created, CRM linked — no manual copy-paste." },
        { name: "measure", desc: "Response time, win rate, margin measured per job. Visible in the dashboard." },
      ],
    },
    outcome: {
      eyebrow: "// OUTCOME",
      h2: "What you have on day 91.",
      cards: [
        { ic: "4 MIN", title: "Response time instead of 24–72 hours.", body: "RFQs are processed autonomously and sit ready for approval while competitors are still opening the Excel file." },
        { ic: "24/7", title: "An inside sales team that never sleeps.", body: "Night, weekend, holiday — the job keeps running. Your people work on customers and margin, not on forms." },
        { ic: "178k", title: "Knowledge stays in the house.", body: "German mechanical engineering will lose 178,000 net skilled workers by 2034. The colleague who ‘has it all in his head' is one of them — his process knowledge keeps running as a system." },
      ],
    },
    proof: {
      eyebrow: "// OWN SYSTEMS LIVE",
      h2: "We don't write about it. We run it.",
      lead: "Two autonomous systems under Raw Response Labs — in production, not on slides.",
      systems: [
        { name: "webblitz.ai", body: "Autonomous AI-visibility system. Continuous monitoring, analysis and optimisation across five AI platforms — with an approve gate before every delivery.", specs: ["pg-boss", "5 AI platforms", "Approve gate"] },
        { name: "Project L.", hidden: true, body: "Agentic newsletter operation. MCP-native. A sponsor agent negotiates and schedules placements autonomously.", specs: ["Agentic", "MCP-native", "Sponsor agent"] },
      ],
      claim: "We know the loop because we run it every day.",
    },
    mid: {
      eyebrow: "// NEXT STEP",
      h3: "Does your quoting process fit into the 90 days?",
      lead: "30 min intro call. We look at your current process and tell you honestly whether and where it pays off.",
      cta: "Book intro call",
    },
    phases: {
      eyebrow: "// PROGRAM",
      h2: "90 days. Three phases. One fixed price. At the end it runs.",
      lead: "No roadmap, no PoC, no ‘let's see'. A clear blueprint with a defined end.",
      p: [
        { days: "DAY 1–30", title: "Process cut.", bullets: ["Pick one job and cut it (quote, complaint, order).", "Clarify data paths & ERP integration.", "Define the approve gate with your sales team."] },
        { days: "DAY 31–60", title: "Build.", bullets: ["Implement the loop, in your cloud, with your data.", "Derive rules & calc from your ERP history.", "Weekly demos — no black-box build."] },
        { days: "DAY 61–90", title: "Live.", bullets: ["Gradual handover: shadow mode, then full ops.", "Runbook, monitoring, alerting.", "Your team runs it. We stay optional as a service."] },
      ],
    },
    buying: {
      eyebrow: "// HOW YOU BUY THIS",
      h2: "No subscription. No per-document fee. No minimum term.",
      p1: "A fixed price for the 90-day build. The system runs in your cloud and belongs to you — operation and further development optional as a monthly service.",
      p2: "Comparable quote automations amortise in months, not years.",
      pills: ["Fixed price", "In your cloud", "You own it", "Ops optional"],
    },
    security: {
      eyebrow: "// SECURITY & CONTROL",
      h2: "Your cloud. Your data. Your approval.",
      lead: "The system runs where your data already lives. No data drain into foreign SaaS silos.",
      cards: [
        { ic: "CLOUD", title: "In your cloud (AWS / Azure / GCP / on-prem).", body: "Deployed in your environment. You keep root, network, keys. GDPR-compliant." },
        { ic: "HITL", title: "Human-in-the-loop gate at critical points.", body: "Approval stays with the human. No quote, no order without your OK." },
        { ic: "AUDIT", title: "Full traceability per job.", body: "Every step logged, every decision reasoned, audit-safe archived." },
      ],
    },
    guarantee: {
      eyebrow: "// GUARANTEE",
      h2: "On day 91 it runs. Or you don't pay.",
      body: "If the system isn't in productive operation after 90 days, you get the fixed price back. No discussion.",
    },
    operator: {
      eyebrow: "// OPERATOR",
      h2a: "Who builds it — and",
      h2b: "who is accountable.",
      lead: "No team, no white-label, no agency chain. One operator who runs the systems himself and is accountable for the outcome.",
      facts: [
        { k: "OPERATOR", v: "Levent Valente — Founder, Raw Response Labs UG" },
        { k: "BACKGROUND", v: "10+ years software & automation. Systems that ship revenue in production." },
        { k: "STACK", v: "TypeScript, Python, pg-boss, MCP, LLM orchestration — in your cloud." },
        { k: "PRINCIPLE", v: "One job, one system, one accountable person. No slides, no PoC carousel." },
      ],
    },
    final: {
      brand: "// RAW RESPONSE LABS × YOUR MACHINE SHOP",
      h2a: "One job. One system.",
      h2b: "One ",
      h2hl: "fixed price.",
      h2c: " 90 days.",
      lead: "30 min intro call. We look at one concrete process. At the end you know if and how it pays off for you.",
      cta: "Book intro call",
      ctaNote: "// No sales pitch. No deck. A look at your process.",
      foerder: "// In the intro call we check which German state funding applies to your project.",
      sticky: "Intro call",
    },
  },
};

/* ---------------- shared ---------------- */

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">
    {children}
  </p>
);

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">
    {children}
  </h2>
);

const Lead = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-2xl">
    {children}
  </p>
);

const SectionDivider = () => (
  <div className="section-divider w-full absolute top-0" />
);

const PrimaryCta = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    className="inline-flex items-center gap-1.5 h-9 px-5 rounded-md bg-primary text-primary-foreground text-[13px] font-mono font-medium hover:bg-primary/90 transition-colors"
  >
    {children}
    <ArrowRight size={13} />
  </a>
);

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" as const } },
};

/* ---------------- hero + loop ---------------- */

const LiveLoop = ({ c }: { c: Copy }) => {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reduced) { setI(3); return; }
    const id = setInterval(() => setI((p) => (p + 1) % c.stages.length), 2400);
    return () => clearInterval(id);
  }, [reduced, c.stages.length]);

  const logKeys = ["detect", "trigger", "draft", "approve", "execute", "measure"] as const;

  return (
    <div className="mt-14 rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-secondary/30">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-border" />
          <div className="h-2 w-2 rounded-full bg-border" />
          <div className="h-2 w-2 rounded-full bg-border" />
          <span className="ml-3 text-[11px] font-mono text-muted-foreground">{c.loop.title}</span>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          {c.loop.running}
        </span>
      </div>

      <div className="px-5 md:px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-stretch gap-6 md:gap-0">
          {c.stages.map((s, idx) => {
            const active = idx === i;
            const done = idx < i;
            return (
              <div key={s.name} className="relative flex-1 md:text-center flex md:block items-center gap-4">
                {idx < c.stages.length - 1 && (
                  <span
                    aria-hidden
                    className={`hidden md:block absolute top-[22px] left-[calc(50%+22px)] right-[calc(-50%+22px)] h-px transition-colors duration-500 ${done ? "bg-primary/40" : "bg-border"}`}
                  />
                )}
                {s.gate && (
                  <span className="hidden md:inline-block absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[9.5px] uppercase tracking-widest text-primary/80">
                    {c.gateTag}
                  </span>
                )}
                <div
                  className={`relative z-10 md:mx-auto h-11 w-11 rounded-lg border flex items-center justify-center font-mono text-[15px] transition-all duration-500 flex-shrink-0 ${
                    active ? "border-primary text-primary bg-primary/5"
                      : done ? "border-primary/40 text-primary/70 bg-card"
                      : "border-border text-muted-foreground bg-card"
                  } ${s.gate ? "border-dashed" : ""}`}
                >
                  {s.icon}
                </div>
                <div>
                  <div className={`md:mt-3 font-mono text-[11px] tracking-wider transition-colors duration-500 ${active ? "text-foreground" : "text-muted-foreground"}`}>
                    {s.name}
                  </div>
                  <div className="mt-0.5 font-mono text-[10.5px] text-muted-foreground/60">{s.sub}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 rounded-md border border-border bg-secondary/20 px-4 py-3 font-mono text-[11.5px] leading-[1.9] text-muted-foreground min-h-[92px]">
          {Array.from({ length: 3 }).map((_, k) => {
            const idx = Math.max(0, i - 2) + k;
            if (idx > i) return null;
            const dim = idx < i;
            const key = logKeys[idx];
            return (
              <div key={idx} className={dim ? "opacity-40" : ""}>
                <span className="text-primary">{key.padEnd(8, " ")}</span>
                {" › "}
                {c.log[key]}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Hero = ({ c }: { c: Copy }) => (
  <header id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28">
    <div className="absolute inset-0 radial-spot pointer-events-none" />
    <div className="container max-w-5xl relative z-10">
      <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">
        {c.eyebrow}
      </p>

      <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-semibold tracking-tight leading-[1.08] mb-6 max-w-3xl">
        <span className="block">{c.h1a}</span>
        <span className="block">
          {c.h1b}
          <span className="text-primary">{c.h1hl}</span>
        </span>
        <span className="block">{c.h1c}</span>
      </h1>

      <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-xl mb-8">
        {c.lead}
      </p>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <PrimaryCta href="/contact">{c.cta}</PrimaryCta>
        <span className="font-mono text-[11.5px] text-muted-foreground/60">{c.ctaNote}</span>
      </div>

      <p className="mt-6 font-mono text-[11.5px] text-muted-foreground/70">
        {c.erpLine}
      </p>

      <div className="mt-14 pt-8 border-t border-border grid gap-6 md:grid-cols-3">
        {c.stats.map((s, n) => (
          <div key={n}>
            <div className="font-mono text-[clamp(1.5rem,2.6vw,2rem)] font-semibold text-primary leading-none">
              {s.num}
            </div>
            <p className="mt-2 font-mono text-[12px] text-muted-foreground leading-relaxed">
              {s.label}
              <span className="mt-0.5 block text-[10.5px] text-muted-foreground/50">{s.src}</span>
            </p>
          </div>
        ))}
      </div>

      <LiveLoop c={c} />
    </div>
  </header>
);

/* ---------------- problem ---------------- */

const Problem = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.problem.eyebrow}</Label>
        <H2>
          {c.problem.h2a}
          <br />
          <span className="text-muted-foreground">{c.problem.h2b}</span>
        </H2>
        <Lead>{c.problem.lead}</Lead>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4">
        {c.problem.cards.map((card, n) => (
          <motion.div key={n} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} className="rounded-xl border border-border bg-card p-6">
            <div className="font-mono text-[10.5px] uppercase tracking-widest text-muted-foreground/60 mb-3">{card.tag}</div>
            <h3 className="text-[14px] font-mono font-medium mb-2">{card.title}</h3>
            <p className="text-[12px] font-mono text-muted-foreground leading-relaxed">{card.body}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 max-w-3xl border-l-2 border-primary/50 pl-5 font-mono text-[12.5px] leading-[1.9] text-muted-foreground">
        <p>{c.problem.note1}</p>
        <p className="mt-3 text-foreground">{c.problem.note2}</p>
      </div>
    </div>
  </section>
);

/* ---------------- mechanism ---------------- */

const Mechanism = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-12">
        <Label>{c.mechanism.eyebrow}</Label>
        <H2>{c.mechanism.h2}</H2>
        <Lead>{c.mechanism.lead}</Lead>
      </motion.div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {c.mechanism.rows.map((row, idx) => (
          <div
            key={row.name}
            className={`grid grid-cols-[40px_1fr] md:grid-cols-[56px_180px_1fr] gap-x-5 gap-y-1.5 items-start px-5 md:px-6 py-5 ${idx > 0 ? "border-t border-border" : ""} ${row.gate ? "bg-primary/[0.03]" : ""}`}
          >
            <div className="font-mono text-[12px] text-muted-foreground/70 pt-0.5">
              {String(idx + 1).padStart(2, "0")}
              {row.gate && " ▸"}
            </div>
            <div className={`font-mono text-[13px] font-medium ${row.gate ? "text-primary" : "text-foreground"}`}>
              {row.name}
            </div>
            <div className="col-span-2 md:col-span-1 text-[13px] font-mono text-muted-foreground leading-relaxed">
              {row.desc}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- outcome ---------------- */

const Outcome = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.outcome.eyebrow}</Label>
        <H2>{c.outcome.h2}</H2>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4">
        {c.outcome.cards.map((card, n) => (
          <div key={n} className="rounded-xl border border-border bg-card p-6">
            <div className="h-8 px-2 inline-flex items-center rounded-md bg-primary/10 font-mono text-[11px] font-medium text-primary mb-4">
              {card.ic}
            </div>
            <h3 className="text-[14px] font-mono font-medium mb-2">{card.title}</h3>
            <p className="text-[12px] font-mono text-muted-foreground leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- proof ---------------- */

const Proof = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.proof.eyebrow}</Label>
        <H2>{c.proof.h2}</H2>
        <Lead>{c.proof.lead}</Lead>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4">
        {c.proof.systems.map((s) => (
          <div key={s.name} className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className={`font-mono text-[13px] font-medium ${s.hidden ? "blur-[5px] select-none" : ""}`} aria-label={s.hidden ? "Unannounced project" : undefined}>{s.name}</div>
              <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-primary">
                <span className={`h-1.5 w-1.5 rounded-full bg-primary ${s.hidden ? "" : "animate-pulse"}`} />
                {s.hidden ? "SNEAK PEEK" : "LIVE"}
              </span>
            </div>
            <div className="p-5">
              <p className="text-[12.5px] font-mono text-muted-foreground leading-relaxed mb-4">{s.body}</p>
              <div className="flex flex-wrap gap-1.5">
                {s.specs.map((sp) => (
                  <span key={sp} className="inline-flex rounded-md border border-border bg-secondary/40 px-2 py-0.5 font-mono text-[10.5px] text-muted-foreground">
                    {sp}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-10 text-center font-mono text-[13px] text-muted-foreground">
        <span className="text-primary font-medium">{c.proof.claim}</span>
      </p>
    </div>
  </section>
);

/* ---------------- mid cta ---------------- */

const MidCTA = ({ c }: { c: Copy }) => (
  <section className="py-16 md:py-20 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-xl border border-border bg-card px-6 py-6 md:px-8 md:py-7">
        <div className="max-w-xl">
          <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-2">{c.mid.eyebrow}</p>
          <h3 className="text-[clamp(1.15rem,2.2vw,1.5rem)] font-semibold tracking-tight leading-snug mb-2">{c.mid.h3}</h3>
          <p className="text-[13px] font-mono text-muted-foreground leading-relaxed">{c.mid.lead}</p>
        </div>
        <div className="shrink-0">
          <PrimaryCta href="/contact">{c.mid.cta}</PrimaryCta>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- phases ---------------- */

const Phases = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.phases.eyebrow}</Label>
        <H2>{c.phases.h2}</H2>
        <Lead>{c.phases.lead}</Lead>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4">
        {c.phases.p.map((ph, n) => (
          <div key={n} className="rounded-xl border border-border bg-card p-6">
            <div className="font-mono text-[10.5px] tracking-widest text-primary mb-3">{ph.days}</div>
            <h3 className="text-[14px] font-mono font-medium mb-3">{ph.title}</h3>
            <ul className="space-y-1.5">
              {ph.bullets.map((b, i) => (
                <li key={i} className="relative pl-4 text-[12px] font-mono text-muted-foreground leading-relaxed">
                  <span aria-hidden className="absolute left-0 top-[7px] h-1 w-1 rounded-sm bg-primary/70" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- buying / anti-abo ---------------- */

const Buying = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl">
        <Label>{c.buying.eyebrow}</Label>
        <H2>{c.buying.h2}</H2>
        <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-2xl mb-4">
          {c.buying.p1}
        </p>
        <p className="text-[14px] font-mono text-foreground leading-relaxed max-w-2xl mb-6">
          {c.buying.p2}
        </p>
        <div className="flex flex-wrap gap-2">
          {c.buying.pills.map((p) => (
            <span key={p} className="inline-flex rounded-md border border-primary/30 bg-primary/[0.06] px-3 py-1 font-mono text-[11.5px] text-primary">
              {p}
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---------------- security ---------------- */

const Security = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.security.eyebrow}</Label>
        <H2>{c.security.h2}</H2>
        <Lead>{c.security.lead}</Lead>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4">
        {c.security.cards.map((card, n) => (
          <div key={n} className="rounded-xl border border-border bg-card p-6">
            <div className="h-8 px-2 inline-flex items-center rounded-md bg-primary/10 font-mono text-[11px] font-medium text-primary mb-4">
              {card.ic}
            </div>
            <h3 className="text-[14px] font-mono font-medium mb-2">{card.title}</h3>
            <p className="text-[12px] font-mono text-muted-foreground leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- guarantee ---------------- */

const Guarantee = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <div className="rounded-xl border border-primary/30 bg-card p-8 md:p-12 text-center max-w-3xl mx-auto">
        <Label>{c.guarantee.eyebrow}</Label>
        <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">
          {c.guarantee.h2}
        </h2>
        <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-xl mx-auto">
          {c.guarantee.body}
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- operator ---------------- */

const Operator = ({ c }: { c: Copy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl grid md:grid-cols-2 gap-12 md:gap-16">
      <div>
        <Label>{c.operator.eyebrow}</Label>
        <H2>
          {c.operator.h2a}
          <br />
          <span className="text-muted-foreground">{c.operator.h2b}</span>
        </H2>
        <Lead>{c.operator.lead}</Lead>
      </div>
      <div className="space-y-3 pt-2">
        {c.operator.facts.map((f) => (
          <div key={f.k} className="grid grid-cols-[110px_1fr] gap-4 text-[13px] font-mono text-muted-foreground">
            <span className="text-[11px] uppercase tracking-widest text-primary">{f.k}</span>
            <span className="leading-relaxed">{f.v}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- final ---------------- */

const FinalCTA = ({ c }: { c: Copy }) => (
  <section id="apply" className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl text-center">
      <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">{c.final.brand}</p>
      <h2 className="mx-auto max-w-2xl text-[clamp(1.75rem,4vw,2.5rem)] font-semibold tracking-tight leading-[1.12] mb-4">
        {c.final.h2a}
        <br />
        {c.final.h2b}
        <span className="text-primary">{c.final.h2hl}</span>
        {c.final.h2c}
      </h2>
      <p className="mx-auto max-w-xl text-[14px] font-mono text-muted-foreground leading-relaxed mb-8">{c.final.lead}</p>
      <div className="flex items-center justify-center">
        <PrimaryCta href="/contact">{c.final.cta}</PrimaryCta>
      </div>
      <p className="mt-4 font-mono text-[11.5px] text-muted-foreground/60">{c.final.ctaNote}</p>
      <p className="mt-2 font-mono text-[11.5px] text-primary/80">{c.final.foerder}</p>
    </div>
  </section>
);

/* ---------------- sticky pill ---------------- */

const StickyPill = ({ c }: { c: Copy }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 800);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className={`fixed bottom-5 right-5 z-40 transition-all duration-300 ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}>
      <a
        href="/contact"
        className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-primary text-primary-foreground text-[12.5px] font-mono font-medium shadow-lg shadow-primary/20 hover:bg-primary/90 transition-colors"
      >
        {c.final.sticky}
        <ArrowRight size={13} />
      </a>
    </div>
  );
};

const MittelstandLanding = () => {
  const lang = useLang();
  const c = copy[lang];
  return (
    <>
      <Hero c={c} />
      <Problem c={c} />
      <Mechanism c={c} />
      <Outcome c={c} />
      <Proof c={c} />
      <MidCTA c={c} />
      <Phases c={c} />
      <Buying c={c} />
      <Security c={c} />
      <Guarantee c={c} />
      <Operator c={c} />
      <FinalCTA c={c} />
      <StickyPill c={c} />
    </>
  );
};

export default MittelstandLanding;
